import { gql } from '@apollo/client';
import { Stack, chakra } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { BigRadio, BigRadioGroup } from '@/components/BigRadio';
import { LogInSignUpModal } from '@/components/LogInSignUp';
import { PaywallModal } from '@/components/Paywall';
import { useSponsorPaywallModal } from '@/components/Post/hooks/useSponsorPaywallModal';
import { useShowPostResponses } from '@/hooks/useShowPostResponses';

import { Options_PollV2Fragment } from './graphql-types/Options_PollV2Fragment';

import { OptionVotes } from './components/OptionVotes';
import { usePollOption } from './hooks/usePollOption';

export const Options: React.FC<{ poll: Options_PollV2Fragment }> & {
  pollFragment: DocumentNode;
} = ({ poll }) => {
  const { vote } = usePollOption(poll);

  const sponsorPaywallModalProps = useSponsorPaywallModal(poll);

  const { behaviorOverride, paywallDisclosure, loginDisclosure } =
    useShowPostResponses(poll);

  const sortedOptions = React.useMemo(() => {
    if (poll.options) {
      return poll.options.slice().sort((a, b) => {
        return a.order && b.order ? a.order - b.order : 0;
      });
    }

    return [];
  }, [poll.options]);

  return (
    <>
      <BigRadioGroup
        value={poll.currentUserResponse?.optionId}
        width="full"
        variant="spaced"
      >
        {sortedOptions.map((option) => {
          return (
            <Stack key={option._id} w="full" direction="row" align="center">
              <BigRadio
                value={option._id}
                onChange={async () => {
                  if (!!behaviorOverride) {
                    behaviorOverride();
                  } else {
                    await vote({ optionId: option._id });
                  }
                }}
                isDisabled={!poll.currentUserCanRespondToPost}
              >
                <Stack direction="row">
                  <chakra.span flex="1">{option.description}</chakra.span>
                  {poll.currentUserCanViewPollResults && (
                    <chakra.span color="gray.600">
                      {poll.responseCount > 0
                        ? Math.round(
                            (option.responseCount / poll.responseCount) * 100
                          )
                        : 0}
                      %
                    </chakra.span>
                  )}
                </Stack>
              </BigRadio>
              {poll.currentUserCanViewResponseAuthor &&
                poll.responseCount > 0 && (
                  <OptionVotes option={option} w="15%" />
                )}
            </Stack>
          );
        })}
      </BigRadioGroup>
      <PaywallModal
        isOpen={paywallDisclosure.isOpen}
        onClose={paywallDisclosure.onClose}
        source="respond to post paywall"
        {...sponsorPaywallModalProps}
      />
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />
    </>
  );
};

Options.pollFragment = gql`
  fragment Options_PollV2Fragment on PollV2 {
    _id
    currentUserCanRespondToPost
    author {
      _id
    }
    currentUserCanViewResponseAuthor
    options {
      _id
      description
      responseCount
      order
    }
    currentUserResponse {
      optionId
    }
    responseCount
    currentUserCanViewPollResults
    ...OptionVotes_PollV2Fragment
    ...usePollOption_PollV2Fragment
    ...useShowPostResponses_PostFragment
    ...useSponsorPaywallModal_PostFragment
  }
  ${OptionVotes.pollFragment}
  ${usePollOption.pollFragment}
  ${useShowPostResponses.postFragment}
  ${useSponsorPaywallModal.postFragment}
`;
